import React, { useState, useEffect } from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { displayWithNonZeroDecimals } from "../libs/utils";
import { useWallet } from "@solana/wallet-adapter-react";

interface MainContainerProps {
    children: React.ReactNode;
}

export default function MainContainer({ children }: MainContainerProps) {
    const { connected, publicKey } = useWallet();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const profile = useSelector((state: RootState) => state.profile);

    useEffect(() => {
        if (connected && publicKey) {
            if (profile.pubkey !== publicKey.toBase58()) {
                setIsLoading(true);
                return;
            }
        }
        setIsLoading(false);
    }, [connected, publicKey, profile]);

    return (
        <Box
            sx={{
                width: "100%",
                flex: 1,
                display: "flex",
                flexDirection: "row",
                alignItems: "stretch",
                maxHeight: "calc(100vh - 90px)",
                minHeight: "calc(100vh - 90px)",
            }}
        >
            <Box
                sx={{
                    width: 260,
                    height: "auto",
                    minHeight: "calc(100vh - 90px)",
                    borderRight: "1px solid #1e1e1e",
                    bgcolor: "background.paper",
                    p: 2,
                    flexDirection: "column",
                    overflowY: "auto",
                    display: {
                        xs: "none",
                        lg: "flex",
                    }
                }}
            >
                <Typography variant="h6">My Wallet</Typography>
                <Typography variant="h6">
                    {isLoading ? (
                        <Skeleton variant="rounded" width={120} height={30} />
                    ) : (
                        `$${displayWithNonZeroDecimals(profile.totalBalance, 1)}`
                    )}
                </Typography>
                <Box display="flex" flexDirection="column" gap={2} mt={2}>
                    {isLoading
                        ? Array.from({ length: 5 }).map((_, index) => (
                            <Box key={index} display="flex" justifyContent="space-between">
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Skeleton variant="rounded" width={27} height={27} />
                                    <Skeleton variant="rounded" width={100} height={27} />
                                </Box>
                                <Skeleton variant="rounded" width={70} height={27} />
                            </Box>
                        ))
                        : Object.entries(profile.balances).map(
                            ([mint, balance]: [string, any]) => (
                                <Box key={mint} display="flex" justifyContent="space-between">
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <img
                                            src={balance.imgUri}
                                            alt={balance.symbol}
                                            style={{ width: 30, height: 30, borderRadius: "50%" }}
                                        />
                                        <Typography variant="body1">{balance.symbol}</Typography>
                                    </Box>
                                    <Typography variant="body1">
                                        {displayWithNonZeroDecimals(balance.amount, 1)}
                                    </Typography>
                                </Box>
                            )
                        )}
                </Box>
            </Box>
            <Box
                sx={{
                    flex: 1,
                    height: "100%",
                    overflowY: "auto",
                    maxHeight: "calc(100vh - 90px)",
                }}
            >
                {children}
            </Box>
        </Box>
    );
}
